// Human labels + grouping for the admin Activity log. Kept out of the page so
// the filter bar and table share one source of truth.

// Human labels for audit action keys; falls back to a humanized key.
export const ACTION_LABEL = {
  'auth.login': 'Signed in',
  'contract.create': 'Created a contract',
  'contract.view': 'Viewed a contract',
  'contract.update': 'Edited a contract',
  'contract.delete': 'Deleted a contract',
  'contract.file_upload': 'Uploaded a file',
  'contract.file_download': 'Downloaded a file',
  'contract.file_update': 'Edited a file',
  'contract.file_delete': 'Deleted a file',
  'contract.action': 'Contract action',
  'contract.bulk_update': 'Bulk-updated contracts',
  'contract.bulk_download': 'Bulk-downloaded files',
  'contract.export': 'Exported the contracts register',
  'contract.obligation_create': 'Added an obligation',
  'contract.obligation_update': 'Edited an obligation',
  'contract.obligation_delete': 'Deleted an obligation',
  'contract.structured_field_edit': 'Edited structured data',
  'contract.structured_row_edit': 'Edited a structured-data row',
  'contract.structured_run': 'Ran a structured-data extraction',
  'contract.structured_verify': 'Verified structured data',
  'request.create': 'Created a TPRM request',
  'request.update': 'Updated a TPRM request',
  'request.view': 'Viewed a TPRM request',
  'request.delete': 'Deleted a TPRM request',
  'request.review': 'Reviewed a TPRM request',
  'request.action': 'TPRM request action',
  'request.document_upload': 'Uploaded a TPRM document',
  'request.document_download': 'Downloaded a TPRM document',
  'request.export': 'Exported the TPRM queue',
  'tprm.criteria_change': 'Changed review criteria',
  'key_risk.criteria_change': 'Changed key-risk criteria',
  'intake.start': 'Started an intake',
  'intake.render': 'Generated an intake document',
  'intake.revise': 'Revised an intake document',
  'intake.delete': 'Deleted an intake',
  'review.submit': 'Sent a document for review',
  'review.decision': 'Signed off on a review',
  'review.rerun_triage': 'Re-ran review triage',
  'review.withdraw': 'Withdrew a review',
  'schema.create': 'Created a data schema',
  'schema.update': 'Edited a data schema',
  'schema.delete': 'Deleted a data schema',
  'schema.archive': 'Archived a data schema',
  'schema.restore': 'Restored a data schema',
  'schema.test_run': 'Test-ran a data schema',
  'schema.batch_preview': 'Previewed a batch run',
  'schema.batch_run': 'Started a batch run',
  'schema.export': 'Exported a dataset',
  'api.outputs_read': 'Read a dataset (API key)',
  'api.contract_output_read': 'Read a contract output (API key)',
  'admin.role_change': 'Changed a user role',
  'admin.grant_change': 'Changed access grants',
  'admin.settings_change': 'Changed settings',
  'admin.drive_config_change': 'Changed Drive configuration',
  'admin.file_backfill': 'Ran a file backfill',
  'admin.conversation_delete': 'Deleted a conversation',
  'admin.activity_export': 'Exported the activity log',
  'admin.service_key_create': 'Issued a service API key',
  'admin.service_key_revoke': 'Revoked a service API key',
  'contract.extract.failed': 'Upload extraction failed',
};

// Failure rows: a matched request that ended >= 400 is recorded under the
// action key with a `.failed` suffix; background extraction failures use
// `contract.extract.failed`. The table shows these with a red chip and the
// error message from metadata.
export function isFailedAction(action) {
  return typeof action === 'string' && action.endsWith('.failed');
}

export function actionLabel(action) {
  if (ACTION_LABEL[action]) return ACTION_LABEL[action];
  if (isFailedAction(action)) {
    return `${actionLabel(action.slice(0, -'.failed'.length))} — failed`;
  }
  return (action || '').replace(/[._]/g, ' ');
}

// Field-level diffs (doc-142). Mutation rows carry
// metadata.changed = { field: { from, to } }; the table renders one
// "Field: old → new" line per entry. Values arrive already normalized and
// capped by the backend (lib/audit-diff.js) — render them verbatim.
export function fieldLabel(field) {
  const spaced = String(field || '')
    .replace(/[_.]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : '';
}

export function fieldDiffLines(metadata) {
  const changed = metadata && typeof metadata === 'object' ? metadata.changed : null;
  if (!changed || typeof changed !== 'object' || Array.isArray(changed)) return [];
  return Object.entries(changed)
    .filter(([, change]) => change && typeof change === 'object' && !Array.isArray(change))
    .map(([field, change]) => ({
      field,
      label: fieldLabel(field),
      from: change.from === undefined ? null : change.from,
      to: change.to === undefined ? null : change.to,
    }));
}

// Where an audited entity links to, when it has a viewable page.
export function entityLink(entityType, entityId) {
  if (!entityId) return null;
  if (entityType === 'contract') return `/contracts/${entityId}`;
  if (entityType === 'request') return `/tprm/${entityId}`;
  if (entityType === 'intake') return `/intake/sessions/${entityId}`;
  if (entityType === 'document_review') return `/reviews/${entityId}`;
  return null;
}

// Action key prefix → group heading, for <optgroup>s in the action dropdown.
const ACTION_GROUP_LABELS = {
  auth: 'Auth',
  contract: 'Contracts',
  request: 'Requests',
  review: 'Reviews',
  intake: 'Intake',
  tprm: 'TPRM',
  key_risk: 'Key risks',
  schema: 'Schemas',
  api: 'Read API',
  admin: 'Admin',
};
const ACTION_GROUP_ORDER = ['auth', 'contract', 'request', 'review', 'intake', 'tprm', 'key_risk', 'schema', 'api', 'admin'];

// Group a flat list of action keys into ordered { label, actions } buckets.
export function groupActions(actions) {
  const buckets = new Map();
  for (const action of actions) {
    const prefix = (action || '').split('.')[0];
    if (!buckets.has(prefix)) buckets.set(prefix, []);
    buckets.get(prefix).push(action);
  }
  const known = ACTION_GROUP_ORDER
    .filter((p) => buckets.has(p))
    .map((p) => ({ key: p, label: ACTION_GROUP_LABELS[p], actions: buckets.get(p) }));
  const extra = [...buckets.keys()]
    .filter((p) => !ACTION_GROUP_ORDER.includes(p))
    .sort()
    .map((p) => ({ key: p, label: p ? p[0].toUpperCase() + p.slice(1) : 'Other', actions: buckets.get(p) }));
  return [...known, ...extra];
}

// Entity-type filter options (small, stable set; in sync with entityLink above).
export const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'All targets' },
  { value: 'contract', label: 'Contracts' },
  { value: 'request', label: 'Requests' },
  { value: 'intake', label: 'Intakes' },
  { value: 'document_review', label: 'Reviews' },
];

// Date-range presets. `days` drives a client-computed `from`; 'custom' shows
// explicit date inputs; '' = any time.
export const DATE_RANGE_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '1', label: 'Last 24 hours', days: 1 },
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: 'custom', label: 'Custom range…' },
];
